import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

export default function TreatmentGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream-50">
      <div className="container mx-auto px-6 py-16">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center text-espresso-800 hover:text-espresso-600 mb-8"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Home
        </button>

        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-serif text-espresso-900 mb-8">PRP/PRF Treatment Guide</h1>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow-xl p-8 border border-cream-200">
              <img 
                src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&q=80" 
                alt="PRP Treatment" 
                className="rounded-lg shadow-lg w-full h-48 object-cover mb-8"
              />
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-espresso-900 mb-4">Before Your Treatment</h2>
                <div className="space-y-4">
                  <div className="bg-cream-100 p-6 rounded-lg">
                    <h3 className="font-semibold text-espresso-800 mb-3">One Week Before:</h3>
                    <ul className="space-y-2 text-espresso-700">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                        Avoid blood thinners
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                        No harsh skin treatments
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-cream-100 p-6 rounded-lg">
                    <h3 className="font-semibold text-espresso-800 mb-3">24 Hours Before:</h3>
                    <ul className="space-y-2 text-espresso-700">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                        No alcohol
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                        Avoid sun exposure
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                        Drink plenty of water
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-espresso-900 mb-4">During Treatment</h2>
                <div className="bg-cream-100 p-6 rounded-lg">
                  <ul className="space-y-3 text-espresso-700">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                      Blood draw (similar to lab work)
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                      Topical numbing cream applied
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                      PRP/PRF preparation (30 mins)
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-clay-500 rounded-full mr-3"></span>
                      Treatment application (60 mins)
                    </li>
                  </ul>
                </div>
              </section>
            </div>

            <div className="space-y-8">
              <div className="bg-white rounded-lg shadow-xl p-8 border border-cream-200">
                <h2 className="text-2xl font-semibold text-espresso-900 mb-4">After Your Treatment</h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-espresso-800 mb-3">First 24 Hours:</h3>
                    <ul className="space-y-2 text-espresso-700">
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-clay-500 rounded-full mr-3 mt-2"></span>
                        <span>Expect mild redness and slight swelling</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-clay-500 rounded-full mr-3 mt-2"></span>
                        <span>No makeup application</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-clay-500 rounded-full mr-3 mt-2"></span>
                        <span>Use only recommended skincare products</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-espresso-800 mb-3">First Week:</h3>
                    <ul className="space-y-2 text-espresso-700">
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-clay-500 rounded-full mr-3 mt-2"></span>
                        <span>Avoid direct sun exposure</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-clay-500 rounded-full mr-3 mt-2"></span>
                        <span>Use gentle cleanser only</span>
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-clay-500 rounded-full mr-3 mt-2"></span>
                        <span>No harsh treatments or exfoliants</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-xl p-8 border border-cream-200">
                <h2 className="text-2xl font-semibold text-espresso-900 mb-4">Results Timeline</h2>
                <div className="space-y-4">
                  <div className="flex items-center p-4 bg-cream-100 rounded-lg">
                    <div className="w-16 h-16 bg-clay-500 rounded-full flex items-center justify-center text-white font-bold">
                      2W
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-espresso-800">Initial Glow</h4>
                      <p className="text-espresso-700">First signs of improvement</p>
                    </div>
                  </div>

                  <div className="flex items-center p-4 bg-cream-100 rounded-lg">
                    <div className="w-16 h-16 bg-clay-600 rounded-full flex items-center justify-center text-white font-bold">
                      6W
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-espresso-800">Collagen Boost</h4>
                      <p className="text-espresso-700">Noticeable texture improvement</p>
                    </div>
                  </div>

                  <div className="flex items-center p-4 bg-cream-100 rounded-lg">
                    <div className="w-16 h-16 bg-clay-700 rounded-full flex items-center justify-center text-white font-bold">
                      3M
                    </div>
                    <div className="ml-4">
                      <h4 className="font-semibold text-espresso-800">Full Results</h4>
                      <p className="text-espresso-700">Maximum benefits visible</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}